/** Prosperity Leaders defaults when no template / overrides / API fallback. */
export const PROSPERITY_BRANDING_DEFAULTS = {
  name: 'Prosperity Leaders',
  logo_url: null,
  primary_color: '#1a5c3a',
  secondary_color: '#f5a623',
  background_color: '#ffffff',
  text_color: '#1a1a1a',
  font_family: 'Inter, sans-serif',
  custom_css: null,
  headline: null,
  subtitle: null
}

const BRANDING_STYLE_KEYS = [
  'logo_url',
  'primary_color',
  'secondary_color',
  'background_color',
  'text_color',
  'font_family',
  'custom_css',
  'headline',
  'subtitle'
]

const isNonEmpty = (value) => value != null && String(value).trim() !== ''

/**
 * @param {Record<string, unknown> | null | undefined} customBranding
 */
export const hasCustomBrandingValues = (customBranding) => {
  if (!customBranding || typeof customBranding !== 'object') return false
  return BRANDING_STYLE_KEYS.some((key) => isNonEmpty(customBranding[key]))
}

/**
 * @param {Record<string, unknown> | null | undefined} template
 */
export const hasBrandTemplate = (template) => Boolean(template?.id)

/**
 * Merge template first, then non-empty custom_branding overrides.
 * @param {Record<string, unknown> | null | undefined} template
 * @param {Record<string, unknown> | null | undefined} customBranding
 */
export const mergeEffectiveBranding = (template, customBranding) => {
  const fromTemplate = template || {}
  const overrides = customBranding || {}
  /** @type {Record<string, string | null>} */
  const merged = {}

  for (const key of BRANDING_STYLE_KEYS) {
    if (isNonEmpty(overrides[key])) {
      merged[key] = String(overrides[key]).trim()
    } else if (isNonEmpty(fromTemplate[key])) {
      merged[key] = String(fromTemplate[key]).trim()
    } else {
      merged[key] = null
    }
  }

  return merged
}

/**
 * @param {Record<string, string | null>} branding
 */
export const brandingToCssVars = (branding) => ({
  '--brand-primary': branding.primary_color || PROSPERITY_BRANDING_DEFAULTS.primary_color,
  '--brand-secondary': branding.secondary_color || PROSPERITY_BRANDING_DEFAULTS.secondary_color,
  '--brand-bg': branding.background_color || PROSPERITY_BRANDING_DEFAULTS.background_color,
  '--brand-text': branding.text_color || PROSPERITY_BRANDING_DEFAULTS.text_color,
  '--brand-font': branding.font_family || PROSPERITY_BRANDING_DEFAULTS.font_family
})

export const emptyCustomBranding = () => ({
  logo_url: '',
  primary_color: '',
  secondary_color: '',
  background_color: '',
  text_color: '',
  font_family: '',
  custom_css: '',
  headline: '',
  subtitle: ''
})

/**
 * @param {Record<string, unknown> | null | undefined} raw
 */
export const normalizeCustomBrandingFromDb = (raw) => {
  const base = emptyCustomBranding()
  if (!raw || typeof raw !== 'object') return base
  for (const key of BRANDING_STYLE_KEYS) {
    if (raw[key] != null) base[key] = String(raw[key])
  }
  return base
}

/**
 * Strip empty strings before saving to JSONB.
 * @param {Record<string, string>} fields
 */
export const customBrandingForSave = (fields) => {
  const out = {}
  for (const key of BRANDING_STYLE_KEYS) {
    const v = fields[key]
    if (isNonEmpty(v)) out[key] = String(v).trim()
  }
  return Object.keys(out).length > 0 ? out : null
}
